'use client';

import { useEffect, useMemo, useRef } from 'react';

import { BarChart, Player } from '@asa_xbt/binary-chart';
import { Card } from '@strategyProfile/components/StrategyPageMain/Components/Card';
import { getChartConfig } from '@strategyProfile/components/StrategyPageMain/Panels/MainPanel/Tabs/TradingData/shared/getChartConfig';
import { useLocale, useTranslations } from 'next-intl';

import { PublicStrategy } from '@/api/copy-trading/types';
import {
  ChartType,
  INTERVALS,
  Interval,
  useStrategyChartData,
} from '@/api/copy-trading/useStrategyChartData';
import { Select } from '@/blocks/components/Controls/Select';

import styles from './styles.module.scss';

export function ProfitablityChart({ strategy }: { strategy: PublicStrategy }) {
  const locale = useLocale();
  const tr = useTranslations();
  const config = getChartConfig(locale);
  const refChartContainer = useRef<null | HTMLDivElement>(null);
  const refChart = useRef<BarChart>(
    new BarChart({ ...config, theme: { ...config.theme, up: '#4A6FFF', down: '#FF627F' } }),
  );
  const refPlayer = useRef<undefined | Player>();

  const { interval, setInterval, setChartMounted } = useStrategyChartData({
    strategyId: strategy.strategyId,
    refChartContainer,
    defaultChartType: ChartType.PROFITABILITY,
    defaultInterval: Interval.PROFITABILITY_MONTH,
    onChange(data) {
      // @ts-ignore
      refChart.current.setData(data, INTERVALS[interval].urlParam);
    },
  });

  function mountChart() {
    if (typeof refChartContainer.current && refChartContainer.current?.childElementCount === 0) {
      refPlayer.current = new Player(refChartContainer.current);
      refPlayer.current?.start(refChart.current);
      setChartMounted(true);
    }
  }

  useEffect(mountChart, [setChartMounted]);

  const MemoizedSelect = useMemo(
    () => (
      <Select
        value={interval}
        onChange={(e) => {
          setInterval(e.target.value as Interval);
        }}
        values={[
          {
            label: tr('pages.strategyPage.sections.main.periodSelect.labels.day'),
            value: Interval.PROFITABILITY_DAY,
          },
          {
            label: tr('pages.strategyPage.sections.main.periodSelect.labels.month'),
            value: Interval.PROFITABILITY_MONTH,
          },
        ]}
      />
    ),
    [interval, setInterval, tr],
  );

  return (
    <Card
      className={styles.profitablityCard}
      headingComponent={MemoizedSelect}
      title={tr.rich(
        'pages.strategyPage.sections.main.widgets.main.tabs.tradingData.widgets.profitability.title',
      )}
    >
      <div className={styles.chartContainer} ref={refChartContainer}></div>
    </Card>
  );
}
